import { Link } from 'react-router-dom';
import I18n from 'i18n-js';

import { ERegisterWithEmailFormField } from '@lootboy/lib/forms/useRegisterWithEmailForm';

import Input from '~/components/atoms/FormControls/Input';
import { PasswordInput } from '~/components/atoms/FormControls/PasswordInput';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { CaptchaOverlay } from '~/components/molecules/CaptchaOverlay';
import { ValidationHints } from '~/components/molecules/ValidationHints';
import { passwordValidationSchema } from '~/components/molecules/ValidationHints/validationSchema';

import {
  ACCOUNT_EXISTS_ERROR,
  useEmailRegistrationView,
} from '~/popups/EmailRegisterPopup/useEmailRegistrationView';
import { LOGIN_PATH, REGISTER_PATHNAME } from '~/routes/paths';
import { useAppHistory } from '~/utilities/routing';

import styles from './RegisterWithEmailForm.module.scss';

const EMAIL_REGISTER_FORM_ID = 'email_register_form';

export function EmailRegisterPopup(): JSX.Element {
  const history = useAppHistory();

  const {
    captcha,
    form: { register, isSubmitEnabled, onSubmit, errors, values },
  } = useEmailRegistrationView();

  return (
    <CaptchaOverlay
      showCaptcha={captcha.isRequired}
      dismissCaptcha={captcha.dismiss}
      onChange={captcha.onChange}
    >
      <MenuViewHeader
        title={I18n.t('MenuView_register_bc')}
        onBack={() => history.push(REGISTER_PATHNAME)}
      />
      <ScrollContainer>
        <section className={styles.container}>
          <form
            noValidate
            onSubmit={onSubmit}
            id={EMAIL_REGISTER_FORM_ID}
          >
            <Input
              {...register(ERegisterWithEmailFormField.EMAIL)}
              error={errors[ERegisterWithEmailFormField.EMAIL]?.message}
              placeholder={I18n.t('MenuView_email_placeholder')}
              label={I18n.t('MenuView_email')}
              id={ERegisterWithEmailFormField.EMAIL}
              type="email"
              errorAction={{
                error: ACCOUNT_EXISTS_ERROR,
                component: (
                  <>
                    {I18n.t('MenuView_email_error_already_existing')}
                    <Link
                      to={{
                        pathname: LOGIN_PATH,
                        state: { email: values.submittedEmail },
                      }}
                      className={styles.loginLink}
                    >
                      {I18n.t('common_login')}
                    </Link>
                  </>
                ),
              }}
            />
            <PasswordInput
              {...register(ERegisterWithEmailFormField.PASSWORD)}
              placeholder={I18n.t('MenuView_setpassword')}
              label={I18n.t('MenuView_password')}
              id={ERegisterWithEmailFormField.PASSWORD}
              autoComplete="new-password"
            />
            <PasswordInput
              {...register(ERegisterWithEmailFormField.REPEAT_PASSWORD)}
              error={
                errors[ERegisterWithEmailFormField.REPEAT_PASSWORD]?.message
              }
              placeholder={I18n.t('MenuView_setpassword_repeat_placeholder')}
              label={I18n.t('MenuView_setpassword_repeat')}
              id={ERegisterWithEmailFormField.REPEAT_PASSWORD}
              autoComplete="new-password"
            />
          </form>
          <ValidationHints
            constraints={passwordValidationSchema}
            value={values.password}
          />
        </section>
      </ScrollContainer>
      <footer className={styles.footer}>
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          type="submit"
          form={EMAIL_REGISTER_FORM_ID}
          disabled={!isSubmitEnabled}
          className={styles.submitButton}
        >
          {I18n.t('confirm')}
        </StyledButton>
      </footer>
    </CaptchaOverlay>
  );
}
